import {
    getCriteriaError,
    parseBoolValue,
    parseNumValue,
    SHOP_CRITERIA_ATTRIBUTE_CT_TYPE_BY_NAME,
} from '@libs/commercetools';

import { shopCriteriaSchema } from '@/schemas';
import type {
    AttributeCriteriaGroup,
    AttributeCriteriaLine,
    ShopCategoryForm,
    ShopCategorySubmitPayload,
    ShopCriteriaGroup,
    ShopCriteriaItem,
} from '@/types';

type AttributeCriteriaGroupInput = {
    id?: string;
    lines?: Array<{
        id?: string;
        attribute?: string;
        values?: string[];
    }>;
};

export const createEmptyAttributeCriteriaLine = (): AttributeCriteriaLine => ({
    attribute: '',
    values: [],
});

export const createEmptyAttributeCriteriaGroup = (): AttributeCriteriaGroup => ({
    lines: [createEmptyAttributeCriteriaLine()],
});

export const normalizeAttributeCriteriaGroups = (
    groups: AttributeCriteriaGroupInput[] | undefined
): AttributeCriteriaGroup[] => {
    if (!groups || groups.length === 0) {
        return [createEmptyAttributeCriteriaGroup()];
    }

    return groups.map(group => {
        const lines = (group.lines ?? []).map(line => ({
            attribute: line.attribute ?? '',
            values: line.values ?? [],
        }));

        return {
            lines: lines.length > 0 ? lines : [createEmptyAttributeCriteriaLine()],
        };
    });
};

export class InvalidCriteriaError extends Error {
    readonly name = 'InvalidCriteriaError';

    constructor(message = 'This attribute is not allowed.') {
        super(message);
    }
}

export class IncompleteCriteriaError extends Error {
    readonly name = 'IncompleteCriteriaError';

    constructor() {
        super('Complete all criteria.');
    }
}

const getTrimmedCriteriaLine = (line: AttributeCriteriaLine) => {
    const attribute = line.attribute.trim();
    const values = line.values.map(item => item.trim()).filter(Boolean);

    return { attribute, values };
};

const isPartialCriteriaLine = (line: AttributeCriteriaLine) => {
    const { attribute, values } = getTrimmedCriteriaLine(line);

    return Boolean(attribute) !== values.length > 0;
};

const formatBooleanCriteriaValuesForSubmit = (values: string[]): string[] =>
    values.map(item => {
        const parsed = parseBoolValue(item);

        return parsed === null ? item : String(parsed);
    });

const formatNumberCriteriaValuesForSubmit = (values: string[]): string[] =>
    values.map(item => {
        const parsed = parseNumValue(item);

        return parsed === null ? item : String(parsed);
    });

const formatAttributeCriteriaLineForSubmit = (line: AttributeCriteriaLine): ShopCriteriaItem | null => {
    const { attribute, values: trimmedValues } = getTrimmedCriteriaLine(line);

    if (!attribute || trimmedValues.length === 0) {
        return null;
    }

    const ruleError = getCriteriaError(attribute, trimmedValues);

    if (ruleError) {
        throw new InvalidCriteriaError();
    }

    switch (SHOP_CRITERIA_ATTRIBUTE_CT_TYPE_BY_NAME.get(attribute)) {
        case 'boolean':
            return { attribute, value: formatBooleanCriteriaValuesForSubmit(trimmedValues) };
        case 'number':
            return { attribute, value: formatNumberCriteriaValuesForSubmit(trimmedValues) };
        default:
            return {
                attribute,
                value: trimmedValues,
            };
    }
};

export const formatAttributeCriteriaGroupForSubmit = (group: AttributeCriteriaGroup): ShopCriteriaItem[] => {
    if (group.lines.some(isPartialCriteriaLine)) {
        throw new IncompleteCriteriaError();
    }

    return group.lines
        .map(formatAttributeCriteriaLineForSubmit)
        .filter((item): item is ShopCriteriaItem => item !== null);
};

export const formatShopCriteriaForSubmit = (groups: AttributeCriteriaGroup[]): ShopCriteriaGroup =>
    groups.map(formatAttributeCriteriaGroupForSubmit).filter(group => group.length > 0);

const formatCommaSeparatedValuesForSubmit = (value: string): string[] =>
    value
        .split(',')
        .map(item => item.trim())
        .filter(Boolean);

export const formatShopCategoryForSubmit = (values: ShopCategoryForm): ShopCategorySubmitPayload => ({
    categoryKey: values.categoryKey.trim(),
    groups: formatShopCriteriaForSubmit(values.attributeCriteriaGroups),
    include: formatCommaSeparatedValuesForSubmit(values.includedWebstyles),
    exclude: formatCommaSeparatedValuesForSubmit(values.excludedWebstyles),
});

export const parseShopCriteriaToFormValues = (shopCriteria: string | undefined) => {
    if (!shopCriteria?.trim()) {
        return null;
    }

    try {
        const result = shopCriteriaSchema.safeParse(JSON.parse(shopCriteria));
        if (!result.success) {
            return null;
        }

        const { groups, include, exclude } = result.data;

        return {
            attributeCriteriaGroups: normalizeAttributeCriteriaGroups(
                groups.map(group => ({
                    lines: group.map(item => ({
                        attribute: item.attribute,
                        values: item.value,
                    })),
                }))
            ),
            includedWebstyles: (include ?? []).join(', '),
            excludedWebstyles: (exclude ?? []).join(', '),
        };
    } catch {
        return null;
    }
};
